import { extractRecipeSizeLabel, normalizeSizeLabel } from "@/lib/manufacturing/groupMembers";
import { composeComponentTags, composeProductTags } from "./composeTags";
import { resolveSlotFill, slugFillDescription } from "./predicates";
import type { ComposeNode, ComposePlan, ComposeSlotDef } from "./types";

let _seq = 0;
function nextId(prefix: string): string {
  _seq += 1;
  return `${prefix}-td-${_seq}-${Math.random().toString(36).slice(2, 7)}`;
}

export type TopDownSkuLine = {
  code: string;
  name: string;
  sizeLabel: string | null;
  styleKey: string | null;
};

/** Parse `CODE | Name` or code-only lines. */
export function parseTopDownSkuList(raw: string): TopDownSkuLine[] {
  const lines = raw
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  const out: TopDownSkuLine[] = [];
  const seen = new Set<string>();
  for (const line of lines) {
    const pipe = line.indexOf("|");
    const code = (pipe >= 0 ? line.slice(0, pipe) : line).trim();
    const name = (pipe >= 0 ? line.slice(pipe + 1) : code).trim() || code;
    if (!code || seen.has(code.toUpperCase())) continue;
    seen.add(code.toUpperCase());
    const sizeLabel =
      extractRecipeSizeLabel({ code, name, tags: [] }) ??
      normalizeSizeLabel(code) ??
      null;
    const styleKey = guessStyleFromCode(code, name);
    out.push({ code, name, sizeLabel, styleKey });
  }
  return out;
}

function guessStyleFromCode(code: string, name: string): string | null {
  const hay = `${code} ${name}`.toLowerCase();
  for (const style of ["utility", "lofted", "studio"]) {
    if (hay.includes(style)) {
      return style.charAt(0).toUpperCase() + style.slice(1);
    }
  }
  const parts = code.split("-");
  if (parts.length >= 3) {
    const last = parts[parts.length - 1];
    if (last && !/^\d/.test(last)) return last;
  }
  return null;
}

type SharedKey = string;

function sharedKey(
  slot: ComposeSlotDef,
  seriesCode: string,
  sizeLabel: string | null,
  styleKey: string | null,
  variantKey: string | null,
): SharedKey {
  return [
    slot.key,
    slot.seriesSpecific ? seriesCode : "",
    slot.sizeSpecific ? sizeLabel ?? "" : "",
    slot.styleSpecific ? styleKey ?? "" : "",
    variantKey ?? "",
  ].join("|");
}

/**
 * Seed products from top-down SKU paste and attach shared slot components
 * when a size can be inferred. Products without size still appear; slots empty.
 */
export function seedTopDown(plan: ComposePlan): ComposePlan {
  const a = plan.archetype;
  const skus = parseTopDownSkuList(a.skuListRaw);
  if (skus.length === 0) {
    return { ...plan, nodes: [], updatedAt: new Date().toISOString() };
  }

  const seriesCode = a.seriesCode.trim() || "SERIES";
  const seriesName = a.seriesName.trim() || seriesCode;
  const shared = new Map<SharedKey, ComposeNode>();
  const products: ComposeNode[] = [];

  for (const sku of skus) {
    const slotLinks: ComposeNode["slots"] = [];
    if (sku.sizeLabel) {
      for (const slot of a.slots) {
        const { fillDescription, variantKey } = resolveSlotFill(sku.sizeLabel, slot);
        const styleForShared = slot.styleSpecific ? sku.styleKey : null;
        const sizeForShared = slot.sizeSpecific ? sku.sizeLabel : null;
        const key = sharedKey(slot, seriesCode, sizeForShared, styleForShared, variantKey);
        let child = shared.get(key);
        if (!child) {
          const stylePart = styleForShared ? `-${styleForShared.toUpperCase()}` : "";
          const sizePart = sizeForShared ? `-${sizeForShared}` : "";
          const variantPart = variantKey ? `-${variantKey.toUpperCase()}` : "";
          child = {
            id: nextId("cmp"),
            role: "component",
            kind: slot.key === "finish_package" ? "package" : "assembly",
            code: `${seriesCode}-${slot.key.toUpperCase().replace(/_/g, "-")}${sizePart}${stylePart}${variantPart}`,
            name: [
              seriesName,
              slot.label,
              sizeForShared,
              styleForShared,
              fillDescription || variantKey,
            ]
              .filter(Boolean)
              .join(" "),
            sizeLabel: sizeForShared,
            styleKey: styleForShared,
            slotKey: slot.key,
            variantKey,
            tags: composeComponentTags({
              sizeLabel: sizeForShared,
              styleKey: styleForShared,
              slotKey: slot.key,
              variantKey,
              fillSlug: fillDescription
                ? slugFillDescription(fillDescription) || fillDescription
                : null,
            }),
            slots: [],
            match: { status: "unmatched" },
          };
          shared.set(key, child);
        }
        slotLinks.push({
          slotKey: slot.key,
          label: slot.label,
          childNodeId: child.id,
        });
      }
    }

    products.push({
      id: nextId("prd"),
      role: "product",
      kind: "product",
      code: sku.code,
      name: sku.name,
      sizeLabel: sku.sizeLabel,
      styleKey: sku.styleKey,
      slotKey: null,
      variantKey: null,
      tags: composeProductTags(sku.sizeLabel, sku.styleKey),
      slots: slotLinks,
      match: { status: "unmatched" },
    });
  }

  return {
    ...plan,
    nodes: [...shared.values(), ...products],
    updatedAt: new Date().toISOString(),
  };
}
